import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalContext } from '../../context/PortalContext';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { benefitCardHeadingStyle } from '../ConstantsStyles';
import { IconBrandAndSymbol } from '../IconBrandAndSymbol';
import { PortalIdentifier } from '../Portal';
import { isUmrMember } from '../Utils/memberUtils/memberUtils';
import {
  CrisisSupportBox,
  CrisisSupportCardCta,
  CrisisSupportIconContainer,
  crisisSupportCardContainerStyle,
  crisisSupportCardContentStyle,
  crisisSupportCardStyle,
} from './CrisisSupport.style';

export type CrisisSupportProps = {
  data: any[];
  index: number;
};

const cardsBackgrounds = {
  0: '$tint2',
  1: '$pastel4',
  2: '$pastel1',
};

const IconMapping = {
  mental_health: 'suicideAndCrisisLifelineIcon',
  care: 'substanceUseIcon',
  ambulance: 'emergencyServicesIcon',
};

export const CrisisSupportCard = ({ data, index }) => {
  const { t } = useTranslation();
  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;
  const isUmr = isUmrMember(portalData?.portalName);
  return (
    <CrisisSupportBox
      aria-hidden="false"
      css={{
        ...crisisSupportCardStyle,
        background: cardsBackgrounds[index % 3],
      }}
      data-auto-testid="crisis-support-card"
      data-testid="crisis-support-card"
    >
      <Flex css={crisisSupportCardContainerStyle}>
        <Heading
          css={benefitCardHeadingStyle}
          data-testid="crisis-support-card-heading"
          display="h6"
          offset={2}
        >
          {data?.suicideAndCrisisLifelineHeader}
        </Heading>
        <Text css={crisisSupportCardContentStyle(isGEHAV2)}>
          {data?.suicideAndCrisisLifelineContent}
        </Text>
        {isUmr ? (
          <CrisisSupportCardCta>
            {data?.suicideAndCrisisLifelineCta === '988' ? (
              <>
                {t('Call')}{' '}
                <a
                  data-testid="crisis-support-card-cta-link-call"
                  href={`tel:${data?.suicideAndCrisisLifelineCta}`}
                  target="_self"
                >
                  {data?.suicideAndCrisisLifelineCta}
                </a>{' '}
                {t('or')} {t('Text')}{' '}
                <a
                  data-testid="crisis-support-card-cta-link_text"
                  href={`sms:${data?.suicideAndCrisisLifelineCta}`}
                  target="_self"
                >
                  {data?.suicideAndCrisisLifelineCta}
                </a>
              </>
            ) : (
              <>
                {t('Call')}{' '}
                <a
                  data-testid="crisis-support-card-cta-link-call"
                  href={`tel:${data?.suicideAndCrisisLifelineCta}`}
                  target="_self"
                >
                  {data?.suicideAndCrisisLifelineCta}
                </a>
              </>
            )}
          </CrisisSupportCardCta>
        ) : (
          <CrisisSupportCardCta
            dangerouslySetInnerHTML={{
              __html: data?.suicideAndCrisisLifelineCta,
            }}
          />
        )}
      </Flex>

      {data?.icon ? (
        <CrisisSupportIconContainer isGeha={isGehaTheme}>
          <IconBrandAndSymbol
            icon={IconMapping[data?.icon] ?? data?.icon}
            size={102}
          />
        </CrisisSupportIconContainer>
      ) : null}
    </CrisisSupportBox>
  );
};
